import React, { useMemo } from "react";
import dayjs from "dayjs";
import _ from "lodash";
import cn from "classnames";

// Assets
import IArrowPlay from "assets/i-arrow-play.js";
import Button from "components/v1/Button";
import IEmpty from "assets/empty-state-listing-match.png";

// Components
import { nominalBilangan } from "utils/currencyFormatter";

const widthContainer = 782;

// Class
const headerRowClassName =
  "flex items-center px-4 py-2 gap-[16px] bg-[#FaFaFa] border-b-[1px] border-solid border-[#D8D8D9]";
const headerColClassName =
  "text-[12px] leading-[18px] tracking-[-0.4px] font-inter text-[#9E9E9E]";

const bodyRowClassName =
  "flex items-center px-4 py-[24px] gap-[16px] border-b border-solid border-[#ECECEC]";
const bodyColClassName =
  "text-[14px] leading-[20px] tracking-[-0.4px] font-inter text-[#101010] truncate";

const checkboxClassName =
  "w-[16px] h-[16px] rounded-[4px] border border-solid border-[#808285] bg-white appearance-none checked:bg-[#02AC5B] checked:border-[#02AC5B] cursor-pointer flex items-center justify-center checked:after:content-['✓'] checked:after:text-white checked:after:text-[12px] checked:after:font-bold";

const TableListListing = ({
  data,
  totalData,
  page,
  handleNextPage,
  handlePrevPage,
  onSelectListing,
  listDetail,
  selectedListings,
  setSelectedListings,
  filter,
}) => {
  const isShowCheckbox = useMemo(() => {
    return (
      filter === "inactive" || filter === "terjual" || filter === "tersewa"
    );
  }, [filter]);

  const Size = useMemo(() => {
    if (!isShowCheckbox) {
      return {
        createdDate: 151,
        marketing: 121,
        area: 121,
        harga: 121,
        jenisProperti: 145,
      };
    }

    return {
      checkbox: 24,
      createdDate: 145,
      marketing: 115,
      area: 115,
      harga: 115,
      jenisProperti: 145,
    };
  }, [isShowCheckbox]);

  const _getSize = (size) => {
    return `${(size / widthContainer) * 100}%`;
  };

  const handleTanggalPelaporan = (date = "") => {
    if (!date) return "";
    let result = dayjs(date).format("D MMM, YYYY");

    if (result === "Invalid Date") {
      const newDate = date.replace(/-/g, "/");
      result = dayjs(newDate).format("D MMM, YYYY");
    }

    return result;
  };

  const handleArea = (locations) => {
    if (!!locations?.length) {
      return locations[0]?.area?.name ?? "";
    }
    return "";
  };

  const LIST_LISTING = useMemo(() => {
    if (!data?.length) return [];

    return data.map((list) => {
      return {
        id: list?.id,
        isPjp: !!list.is_pjp,
        createdDate: handleTanggalPelaporan(list.updated_at),
        marketing: list?.user?.full_name ?? "",
        area: handleArea(list?.summary?.property_locations ?? []),
        harga: nominalBilangan(parseInt(list?.price ?? 0)),
        jenisProperti: list?.property_category?.name,
        tipeProperty: _.get(list, "property_type.id") === 1 ? "J" : "S",
        slug: list?.slug,
      };
    });
  }, [data]);

  const TextEmpty = useMemo(() => {
    if (filter === "inactive") {
      return {
        title: "Tidak ada listing tidak aktif",
        subtitle: "Listing yang tidak aktif akan ditampilkan di sini.",
      };
    }
    if (filter === "active") {
      return {
        title: "Tidak ada listing aktif",
        subtitle: "Listing yang aktif akan ditampilkan di sini.",
      };
    }
    if (filter === "terjual") {
      return {
        title: "Tidak ada listing terjual",
        subtitle: "Listing yang terjual akan ditampilkan di sini.",
      };
    }
    if (filter === "tersewa") {
      return {
        title: "Tidak ada listing tersewa",
        subtitle: "Listing yang tersewa akan ditampilkan di sini.",
      };
    }
    return {
      title: "Tidak ada listing",
      subtitle: "Listing akan ditampilkan di sini.",
    };
  }, [filter]);

  return (
    <>
      <div className={`min-w-[${widthContainer}px] min-h-[725px]`}>
        {/* Header */}
        <div className={headerRowClassName}>
          {isShowCheckbox && (
            <div
              className={headerColClassName}
              style={{ width: _getSize(Size.checkbox) }}
            >
              <input
                className={checkboxClassName}
                type="checkbox"
                checked={selectedListings?.length > 0}
                onChange={() => {
                  if (selectedListings?.length > 0) {
                    setSelectedListings([]);
                  } else {
                    setSelectedListings(data.map((list) => list.id));
                  }
                }}
              />
            </div>
          )}
          <div
            className={headerColClassName}
            style={{ width: _getSize(Size.createdDate) }}
          >
            Tanggal diperbarui
          </div>
          <div
            className={headerColClassName}
            style={{ width: _getSize(Size.marketing) }}
          >
            Marketing
          </div>
          <div
            className={headerColClassName}
            style={{ width: _getSize(Size.area) }}
          >
            Area
          </div>
          <div
            className={headerColClassName}
            style={{ width: _getSize(Size.harga) }}
          >
            Harga
          </div>
          <div
            className={headerColClassName}
            style={{ width: _getSize(Size.jenisProperti) }}
          >
            Jenis Property
          </div>
        </div>

        {/* Body */}
        {LIST_LISTING?.length ? (
          LIST_LISTING.map((item) => {
            const isSelected = listDetail?.id === item.id;
            const isChecked = selectedListings?.includes(item.id);
            return (
              <div
                key={`table-list-listing-${item.id}`}
                className={cn(bodyRowClassName, {
                  "bg-[#F6F6F6]": isSelected,
                })}
                onClick={() => onSelectListing(item.slug)}
              >
                {isShowCheckbox && (
                  <div
                    className={bodyColClassName}
                    style={{ width: _getSize(Size.checkbox) }}
                  >
                    <input
                      className={checkboxClassName}
                      type="checkbox"
                      checked={isChecked}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => {
                        if (isChecked) {
                          setSelectedListings(
                            selectedListings.filter(
                              (listingId) => listingId !== item.id
                            )
                          );
                        } else {
                          setSelectedListings([...selectedListings, item.id]);
                        }
                      }}
                    />
                  </div>
                )}
                <div
                  className={cn(bodyColClassName, "flex items-center gap-1")}
                  style={{ width: _getSize(Size.createdDate) }}
                >
                  <span
                    className={cn(
                      "bg-[#BEAF87] rounded-[100px] w-[32px] h-[16px] flex items-center justify-center py-0.5 px-1 text-[12px] font-inter-bold text-white",
                      {
                        invisible: !item.isPjp,
                      }
                    )}
                  >
                    PJP
                  </span>
                  <span className="text-[11px]">{item.createdDate}</span>
                </div>
                <div
                  className={bodyColClassName}
                  style={{ width: _getSize(Size.marketing) }}
                >
                  {item.marketing}
                </div>
                <div
                  className={bodyColClassName}
                  style={{ width: _getSize(Size.area) }}
                >
                  {item.area}
                </div>
                <div
                  className={bodyColClassName}
                  style={{ width: _getSize(Size.harga) }}
                >
                  {item.harga}
                </div>
                <div
                  className={cn(bodyColClassName, "flex items-center gap-1")}
                  style={{ width: _getSize(Size.jenisProperti) }}
                >
                  <span className="w-4 h-4 rounded-[100px] flex items-center justify-between border-[0.5px] border-solid border-[#808285] bg-[#E6E7E8] p-0.5 text-[12px] leading-[20px] tracking-[-0.4px] font-inter text-[#252526]">
                    {item.tipeProperty}
                  </span>
                  <span className="text-[12px] leading-[20px] tracking-[-0.4px] font-inter text-[#252526]">
                    {item.jenisProperti}
                  </span>
                </div>
              </div>
            );
          })
        ) : (
          <div className="h-[725px] flex flex-col items-center justify-center gap-0.5 py-6">
            <img
              className="w-[48px] h-[48px] object-contain"
              src={IEmpty}
              alt="empty-state"
            />
            <p className="text-[13px] leading-[21px] tracking-[-0.4px] font-inter-semibold text-[#252526]">
              {TextEmpty.title}
            </p>
            <p className="text-[11px] leading-[17.798px] tracking-[-0.4px] font-inter text-[#808285]">
              {TextEmpty.subtitle}
            </p>
          </div>
        )}
      </div>

      <div
        className={`px-4 pb-2 flex items-center justify-end mt-6 min-w-[${widthContainer}px]`}
      >
        <div className="flex items-center gap-[32px]">
          <Button
            text="Sebelumnya"
            onClick={handlePrevPage}
            icon={IArrowPlay}
            type="secondary"
            size="l"
            disabled={page === 1}
          />
          <p className="text-[14px] leading-[20px] font-inter text-[#101010]">
            {page} <span className="text-[#676767]">dari</span> {totalData}{" "}
            <span className="text-[#676767]">halaman</span>
          </p>
          <Button
            text="Selanjutnya"
            onClick={handleNextPage}
            icon={IArrowPlay}
            rotateIcon="180"
            type="secondary"
            size="l"
            disabled={page === totalData}
            iconPosition="right"
          />
        </div>
      </div>
    </>
  );
};

export default TableListListing;
